import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '../components/Button'
import { useListEventsQuery } from '@proaxiv/server-api'
import { useAppSelector } from '../store/hooks'
import { fromEventSummary } from './event/eventMapping'
import type { ListEventsResponse } from './event/eventMapping'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { useRequireEventPlannerFeature } from '../hooks/useRequireEventPlannerFeature'
import { formatEventDateTime } from '../utils/datetime'

// ── Icons ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

// ── Page ──────────────────────────────────────────────────────────────────────

/** Event Planner dashboard — lists all events; each card links through to its management page. */
export function EventPlannerPage() {
  const { t, i18n } = useTranslation()
  useRequireEventPlannerFeature()
  const { activeOrgId, eventPlannerQuota } = useActiveOrg()

  const {
    data: listRaw,
    isLoading,
    isError,
  } = useListEventsQuery({ orgId: activeOrgId, page: 0, size: 50 }, { skip: !activeOrgId })
  const listResponse = listRaw as ListEventsResponse | undefined
  const apiEvents = listResponse?.events?.map((summary) => fromEventSummary(summary))
  // Use API data when available; fall back to the Redux store for immediate
  // display (covers tests and the in-app navigation case).
  const eventsFromStore = useAppSelector((s) => s.events.list)
  const events = apiEvents ?? eventsFromStore

  const isAtQuotaLimit =
    eventPlannerQuota !== null &&
    eventPlannerQuota.maxUsage !== Number.MAX_SAFE_INTEGER &&
    eventPlannerQuota.currentUsage >= eventPlannerQuota.maxUsage

  const quotaLabel =
    eventPlannerQuota !== null && eventPlannerQuota.maxUsage !== Number.MAX_SAFE_INTEGER
      ? `${eventPlannerQuota.currentUsage} / ${eventPlannerQuota.maxUsage} events used`
      : null

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
            {t('ep_title')}
          </h1>
          <p className="text-pax-gray text-sm">{t('ep_subtitle')}</p>
          {quotaLabel && (
            <p
              className={`mt-0.5 text-xs font-medium ${isAtQuotaLimit ? 'text-red-500' : 'text-pax-gray'}`}
            >
              {quotaLabel}
            </p>
          )}
        </div>
        {!isLoading &&
          !isError &&
          events.length > 0 &&
          (isAtQuotaLimit ? (
            <ButtonLink
              variant="primary"
              to="/event-planner/new"
              leftIcon={<PlusIcon />}
              aria-disabled="true"
              onClick={(e) => e.preventDefault()}
              className="cursor-not-allowed opacity-40"
              title="Event limit reached for this organisation"
            >
              {t('ep_new_btn')}
            </ButtonLink>
          ) : (
            <ButtonLink variant="primary" to="/event-planner/new" leftIcon={<PlusIcon />}>
              {t('ep_new_btn')}
            </ButtonLink>
          ))}
      </div>

      {/* Loading / error */}
      {isLoading && (
        <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
          <div className="bg-pax-purple/10 mx-auto h-5 w-48 rounded" />
        </div>
      )}
      {isError && (
        <div className="border-pax-red/20 rounded-2xl border bg-red-50 p-6 text-center">
          <p className="text-pax-red text-sm font-medium">Failed to load events. Please refresh.</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && events.length === 0 && (
        <div className="border-pax-purple/20 flex flex-col items-center gap-5 rounded-2xl border bg-white px-8 py-16 text-center shadow-sm">
          <div className="bg-pax-light-blue text-pax-blue flex h-14 w-14 items-center justify-center rounded-2xl">
            <CalendarIcon />
          </div>
          <div>
            <p className="text-pax-black font-semibold">{t('ep_empty_title')}</p>
            <p className="text-pax-gray mt-1 text-sm">{t('ep_empty_subtitle')}</p>
          </div>
          {isAtQuotaLimit ? (
            <ButtonLink
              variant="primary"
              to="/event-planner/new"
              leftIcon={<PlusIcon />}
              aria-disabled="true"
              onClick={(e) => e.preventDefault()}
              className="cursor-not-allowed opacity-40"
              title="Event limit reached for this organisation"
            >
              {t('ep_new_btn')}
            </ButtonLink>
          ) : (
            <ButtonLink variant="primary" to="/event-planner/new" leftIcon={<PlusIcon />}>
              {t('ep_new_btn')}
            </ButtonLink>
          )}
        </div>
      )}

      {/* Event list */}
      {!isLoading && !isError && events.length > 0 && (
        <div className="flex flex-col gap-3">
          {events.map((ev) => (
            <Link
              key={ev.id}
              to={`/event-planner/${ev.id}`}
              className="border-pax-purple/20 hover:border-pax-blue/30 hover:bg-pax-light-blue/30 focus-visible:ring-pax-blue/50 flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-sm transition-colors focus-visible:ring-2 focus-visible:outline-none sm:gap-4 sm:p-5"
            >
              {/* Logo / icon */}
              <div className="border-pax-purple/20 bg-pax-light-purple flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border">
                {ev.branding.logo ? (
                  <img
                    src={ev.branding.logo.dataUrl}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="text-pax-blue">
                    <CalendarIcon />
                  </div>
                )}
              </div>

              {/* Info — plain text (not a nested interactive element) so it stays selectable,
                  e.g. to copy the title or date; clicking anywhere else on the card still
                  navigates like a normal link. */}
              <div className="min-w-0 flex-1 select-text">
                <div className="flex items-start gap-2 sm:items-center">
                  <p className="text-pax-black line-clamp-2 text-sm font-semibold sm:truncate sm:text-base">
                    {ev.title}
                  </p>
                  <span
                    className={[
                      'mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:mt-0',
                      ev.status === 'published'
                        ? 'bg-pax-green/15 text-pax-green'
                        : 'bg-pax-orange-warm/15 text-pax-orange-warm',
                    ].join(' ')}
                  >
                    {ev.status === 'published' ? t('ep_status_published') : t('ep_status_draft')}
                  </span>
                </div>
                <p className="text-pax-gray mt-0.5 text-xs">
                  {ev.startDateTime
                    ? formatEventDateTime(ev.startDateTime, ev.timezone, i18n.language)
                    : t('ep_no_date')}
                  {ev.address ? ` · ${ev.address}` : ''}
                </p>
              </div>

              {/* Chevron — affordance indicating the whole card opens the event page */}
              <span className="text-pax-gray shrink-0" aria-hidden="true">
                <ChevronRightIcon />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
